/**
 * TanStack Query hooks for the changelog API.
 */

import { useQuery } from "@tanstack/react-query";
import {
  fetchChangelog,
  fetchNodeChangelog,
  type ChangelogResponse,
  type ChangeEvent,
  type ChangelogOptions,
} from "../api/changelog";
import { useIsVisible } from "./useIsVisible";

const DEFAULT_OPTIONS: ChangelogOptions = {};

export function useChangelog(
  projectId: string,
  branch: string,
  options: ChangelogOptions = DEFAULT_OPTIONS,
  enabled = true,
) {
  const isVisible = useIsVisible();
  const isPollingEnabled = enabled && isVisible;

  return useQuery<ChangelogResponse>({
    queryKey: [
      "changelog",
      projectId,
      branch,
      options.limit,
      options.offset,
      options.eventTypes,
      options.since,
      options.until,
    ],
    queryFn: () => fetchChangelog(projectId, branch, options),
    enabled: isPollingEnabled,
    refetchInterval: isPollingEnabled ? 30_000 : false,
  });
}

export function useNodeChangelog(
  nodeId: string | null,
  projectId: string,
  branch = "main",
) {
  return useQuery<ChangeEvent[]>({
    queryKey: ["node-changelog", nodeId, projectId, branch],
    queryFn: () => fetchNodeChangelog(nodeId!, projectId, branch),
    enabled: !!nodeId,
  });
}
